/**
 * EmbeddedMintingDapp Component
 *
 * Main wrapper component that dispatches to appropriate renderer
 * based on dapp type (internal vs external).
 *
 * @module minting/components/EmbeddedMintingDapp
 * @category Minting Framework
 */

import React from 'react';
import { useMintingDapp } from '../hooks/useMintingDapp';
import { ExternalDappIframe } from './ExternalDappIframe';
import { InternalDappRenderer } from './InternalDappRenderer';

/**
 * Props for EmbeddedMintingDapp
 */
export interface EmbeddedMintingDappProps {
  /** The ID of the minting dapp to embed */
  dappId: string;
  /** Optional CSS class name */
  className?: string;
  /** Optional style overrides */
  style?: React.CSSProperties;
}

/**
 * EmbeddedMintingDapp - Main component for embedding minting dapps.
 *
 * This component automatically selects the appropriate renderer:
 * - Internal dapps: Rendered as React component (no iframe)
 * - External dapps: Rendered in iframe with postMessage bridge
 *
 * Shows an error state if dapp is not found.
 *
 * @example
 * ```tsx
 * <EmbeddedMintingDapp dappId="mint-nft" className="h-100" />
 * ```
 */
export const EmbeddedMintingDapp: React.FC<EmbeddedMintingDappProps> = ({
  dappId,
  className = '',
  style = {},
}) => {
  const dapp = useMintingDapp(dappId);

  // Not found state
  if (!dapp) {
    return (
      <div
        className={`embedded-dapp-not-found ${className}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '400px',
          backgroundColor: '#1a1a1a',
          ...style,
        }}
      >
        <div className="text-center p-4">
          <i className="fas fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <h4 className="text-light mb-2">Minting Dapp Not Found</h4>
          <p className="text-light opacity-6 mb-0">
            The application "{dappId}" is not registered or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Dispatch to appropriate renderer based on type
  switch (dapp.type) {
    case 'internal':
      return (
        <InternalDappRenderer
          dappId={dappId}
          className={className}
          style={style}
        />
      );

    case 'external':
      if (!dapp.url) {
        return (
          <div
            className={`embedded-dapp-error ${className}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              backgroundColor: '#1a1a1a',
              ...style,
            }}
          >
            <div className="text-center p-4">
              <i className="fas fa-times-circle fa-3x text-danger mb-3"></i>
              <h5 className="text-light">Invalid Configuration</h5>
              <p className="text-light opacity-6 small">
                This external dapp has no URL configured.
              </p>
            </div>
          </div>
        );
      }
      return (
        <ExternalDappIframe
          dapp={dapp}
          className={className}
          style={style}
        />
      );

    default:
      return (
        <div
          className={`embedded-dapp-unknown ${className}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            backgroundColor: '#1a1a1a',
            ...style,
          }}
        >
          <div className="text-center p-4">
            <i className="fas fa-question-circle fa-3x text-info mb-3"></i>
            <h5 className="text-light">Unknown Dapp Type</h5>
            <p className="text-light opacity-6 small">
              The dapp type "{dapp.type}" is not supported.
            </p>
          </div>
        </div>
      );
  }
};
